import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Check, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { showErrorToast, showSuccessToast } from '@/utils/toastUtils';
import { debugLogger } from '@/utils/debugLogger';

interface EmailVerificationCardProps {
  verification: any;
  onUpdate: () => void;
}

interface ResendDelayResponse {
  can_resend: boolean;
  attempt_number: number;
  delay_seconds: number;
  seconds_remaining: number;
  next_allowed_at: string;
  last_attempt_at?: string;
}

const EmailVerificationCard = ({ verification, onUpdate }: EmailVerificationCardProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(user?.email || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const isVerified = verification?.email_verified === true;

  // Check rate limit on mount
  useEffect(() => {
    const checkRateLimit = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase.rpc('get_email_resend_delay', {
          p_user_id: user.id
        } as any);

        if (error) {
          debugLogger.error('verification', 'Error checking rate limit', error);
          return;
        }

        if (data) {
          const delayData = data as unknown as ResendDelayResponse;
          
          debugLogger.debug('verification', 'Rate limit status on mount', {
            can_resend: delayData.can_resend,
            attempt_number: delayData.attempt_number,
            seconds_remaining: delayData.seconds_remaining,
            next_allowed_at: delayData.next_allowed_at,
            last_attempt_at: delayData.last_attempt_at
          });
          
          setCanResend(delayData.can_resend);
          setSecondsRemaining(delayData.seconds_remaining || 0);
          
          // Se já existe tentativa anterior, mostrar campo de código
          if (delayData.attempt_number > 0) {
            setCodeSent(true);
            debugLogger.debug('verification', 'Previous attempts detected, showing code input', {
              attempt_number: delayData.attempt_number
            });
          }
        }
      } catch (error) {
        debugLogger.error('verification', 'Error checking rate limit', error);
      }
    };

    checkRateLimit();
  }, [user?.id]);

  // Countdown timer
  useEffect(() => {
    if (secondsRemaining > 0) {
      const timer = setInterval(() => {
        setSecondsRemaining(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [secondsRemaining]);

  const sendEmailCode = async () => {
    setErrorMessage('');
    setIsError(false);
    
    if (!email) {
      showErrorToast({ title: 'Erro', description: 'Digite um email válido' });
      return;
    }

    if (!canResend) {
      const minutes = Math.ceil(secondsRemaining / 60);
      showErrorToast({ 
        title: 'Aguarde', 
        description: `Você poderá reenviar o código em ${minutes} minuto(s)` 
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-email-verification', {
        body: { 
          email, 
          verificationId: verification?.id,
          userId: user?.id
        }
      });

      if (error) {
        throw error;
      }

      // Check if response indicates rate limit or success
      if (!data?.success) {
        const errorMsg = data?.error || 'Erro ao enviar código';
        
        debugLogger.debug('verification', 'Rate limit response', {
          success: false,
          error: errorMsg,
          seconds_remaining: data?.seconds_remaining,
          next_allowed_at: data?.next_allowed_at
        });
        
        showErrorToast({ title: 'Erro', description: errorMsg });
        
        if (data?.seconds_remaining) {
          setSecondsRemaining(data.seconds_remaining);
          setCanResend(false);
        }
        return;
      }

      debugLogger.debug('verification', 'Email sent successfully', {
        next_resend_seconds: data?.next_resend_seconds
      });

      showSuccessToast({ title: 'Sucesso', description: 'Código enviado para seu email!' });
      setCodeSent(true);
      
      // Set rate limit from response
      if (data?.next_resend_seconds) {
        setSecondsRemaining(data.next_resend_seconds);
        setCanResend(false);
      }
    } catch (error: any) {
      showErrorToast({ 
        title: 'Erro ao enviar código', 
        description: error.message || 'Erro ao enviar código'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (errorMessage: string) => {
    if (errorMessage.includes('Código inválido') || errorMessage.includes('Invalid code')) {
      return 'Código incorreto. Verifique se digitou corretamente.';
    }
    if (errorMessage.includes('expirado') || errorMessage.includes('expired')) {
      return 'Código expirado. Solicite um novo código clicando em "Reenviar".';
    }
    return 'Erro na verificação. Tente novamente ou solicite um novo código.';
  };

  const verifyEmailCode = async () => {
    setErrorMessage('');
    setIsError(false);
    
    if (!verificationCode) {
      setErrorMessage('Digite o código de verificação');
      setIsError(true);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-email-code', {
        body: { 
          verificationId: verification?.id,
          code: verificationCode
        }
      });

      // Erro de rede ou HTTP 500 (erro técnico)
      if (error) {
        debugLogger.error('verification', 'Technical error calling verify-email-code', error);
        throw error;
      }

      // Resposta bem-sucedida da API (status 200), mas pode ser erro de negócio
      if (!data?.success) {
        const errorMessage = data?.error || 'Erro desconhecido';
        debugLogger.debug('verification', 'Code validation failed (business error)', { errorMessage });
        throw new Error(errorMessage);
      }

      // Sucesso real
      debugLogger.debug('verification', 'Email verified successfully');
      showSuccessToast({ title: 'Sucesso', description: 'Email verificado com sucesso!' });
      onUpdate();
    } catch (error: any) {
      const friendlyMessage = getErrorMessage(error.message || '');
      setErrorMessage(friendlyMessage);
      setIsError(true);
      
      showErrorToast({ 
        title: 'Erro na verificação', 
        description: friendlyMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(newCode);
    
    // Clear error when user starts typing
    if (isError && newCode !== verificationCode) {
      setErrorMessage('');
      setIsError(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getCardStatus = () => {
    if (isVerified) return 'verified';
    if (codeSent) return 'pending';
    return 'waiting';
  };

  const status = getCardStatus();

  return (
    <Card className={`transition-all duration-300 bg-white/5 border-white/10 ${
      status === 'verified' ? 'border-green-500/50 bg-green-500/10' :
      status === 'pending' ? 'border-blue-500/50 bg-blue-500/10' :
      'hover:bg-white/10'
    }`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-white">
          <div className={`p-2 rounded-full ${
            status === 'verified' ? 'bg-green-500 text-white' :
            status === 'pending' ? 'bg-blue-500 text-white' :
            'bg-white/20 text-white/80'
          }`}>
            {status === 'verified' ? <Check className="w-4 h-4" /> :
             status === 'pending' ? <Clock className="w-4 h-4" /> :
             <Mail className="w-4 h-4" />}
          </div>
          Verificação por Email
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {!isVerified && (
          <>
            <div>
              <div className="mb-3 px-3 py-2 bg-white/5 border border-white/20 rounded-md">
                <p className="text-xs text-white/60 mb-1">Email cadastrado</p>
                <p className="text-white text-sm">{email || 'Nenhum email cadastrado'}</p>
              </div>
              
              {!codeSent ? (
                <>
                  <Button 
                    onClick={sendEmailCode}
                    disabled={isLoading || !email || !canResend}
                    className="w-full bg-luxury-red hover:bg-luxury-red/90"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : !canResend ? (
                      <>Aguarde {formatCountdown(secondsRemaining)}</>
                    ) : (
                      'Enviar Código'
                    )}
                  </Button>
                  
                  <p className="text-center mt-2">
                    <button
                      onClick={() => navigate('/main/support')}
                      className="text-[10px] text-white/50 hover:text-white/80 underline transition-colors"
                    >
                      E-mail errado? Fale com o suporte.
                    </button>
                  </p>
                </>
              ) : (
                <div className="space-y-3">
                  <Input
                    placeholder="Código de 6 dígitos"
                    value={verificationCode}
                    onChange={handleCodeChange}
                    maxLength={6}
                    className={`text-center text-lg tracking-widest font-mono bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-luxury-gold/50 ${
                      isError ? 'border-red-500 focus:border-red-500' : ''
                    }`}
                  />
                  
                  {isError && errorMessage && (
                    <Alert variant="destructive" className="mt-2 bg-red-500/10 border-red-500/50 text-red-300">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-red-300">
                        {errorMessage}
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="flex gap-2">
                    <Button 
                      onClick={verifyEmailCode}
                      disabled={isLoading || verificationCode.length !== 6}
                      className="flex-1 bg-luxury-red hover:bg-luxury-red/90"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verificando...
                        </>
                      ) : (
                        'Verificar'
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={sendEmailCode}
                      disabled={isLoading || !canResend}
                      size="sm"
                      className="bg-white/90 border-white/20 text-gray-800 hover:bg-white"
                    >
                      {!canResend ? formatCountdown(secondsRemaining) : 'Reenviar'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {isVerified && (
          <div className="text-center py-4">
            <div className="text-green-400 font-medium flex items-center justify-center gap-2">
              <Check className="w-5 h-5" />
              Email verificado!
            </div>
            <p className="text-sm text-white/60 mt-1">Verificado em {
              new Date(verification.email_verified_at).toLocaleDateString('pt-BR')
            }</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailVerificationCard;
